import { buf2Base64, sha256 } from '@seen/core/utils'
import { createTypedLocalStorage } from '@seen/react'
import { z } from 'zod'
import type { PortalAiClient } from '#src/api/ai.ts'
import type {
  McpOAuthMetadataResponse,
  McpOAuthTokenRequest,
  McpOAuthTokenResponse,
} from '#src/mcp/contract.ts'

/**
 * Browser-side custody of the per-user remote-MCP OAuth session. Tokens live in
 * localStorage (same posture as Medplum's `activeLogin`); the portal server is
 * only used as a CORS proxy for discovery/registration/token calls and never
 * persists anything. The MCP tool calls themselves run server-side, so the
 * browser just hands the current access token to `/api/chat` per request.
 */

// Refresh slightly early so an in-flight chat request never sends a token that
// expires mid-stream.
const EXPIRY_SKEW_MS = 60_000
const TOKENS_KEY = 'mintmcp.oauth.tokens'
const PENDING_KEY = 'mintmcp.oauth.pending'

const tokenSchema = z.object({
  // The dynamically-registered OAuth client this session's tokens belong to.
  // Persisted so refreshes send the same client_id the grant was issued to —
  // DCR is non-idempotent and per-instance, so re-deriving it server-side would
  // mismatch across Vercel instances.
  clientId: z.string(),
  accessToken: z.string(),
  refreshToken: z.string().optional(),
  // Epoch ms; absent when the AS omits expires_in (treated as non-expiring).
  expiresAt: z.number().optional(),
})

type StoredMcpTokens = z.infer<typeof tokenSchema>

const tokenStore = createTypedLocalStorage(TOKENS_KEY, tokenSchema)

const pendingSchema = z.object({
  state: z.string(),
  clientId: z.string(),
  codeVerifier: z.string(),
  redirectUri: z.string(),
  returnTo: z.string(),
})

// ── External store so React reflects connect/disconnect across the app ──────
const listeners = new Set<() => void>()
function emitChange(): void {
  for (const listener of listeners) {
    listener()
  }
}
export function subscribeMcpConnection(listener: () => void): () => void {
  listeners.add(listener)
  const onStorage = (e: StorageEvent) => {
    if (e.key === TOKENS_KEY) {
      listener()
    }
  }
  window.addEventListener('storage', onStorage)
  return () => {
    listeners.delete(listener)
    window.removeEventListener('storage', onStorage)
  }
}
export function isMcpConnected(): boolean {
  return tokenStore.get() !== undefined
}

export function clearMcpTokens(): void {
  tokenStore.remove()
  emitChange()
}

function randomString(byteLength = 32): string {
  return buf2Base64(crypto.getRandomValues(new Uint8Array(byteLength)))
}

async function fetchMetadata(
  client: PortalAiClient
): Promise<McpOAuthMetadataResponse> {
  const res = await client.api.mcp.oauth.metadata.$get()
  if (!res.ok) {
    throw new Error(`Failed to load MCP OAuth metadata (${res.status})`)
  }
  return res.json()
}

/*
 * Kick off the authorization-code + PKCE flow by redirecting the browser to the
 * MCP authorization server. `returnTo` is where the callback page sends the user
 * afterward (defaults to the current location).
 */
export async function beginMcpAuthorization(
  client: PortalAiClient,
  returnTo: string = window.location.pathname + window.location.search
): Promise<void> {
  const metadata = await fetchMetadata(client)
  const codeVerifier = randomString()
  const state = randomString(16)
  const challenge = await sha256(codeVerifier)

  sessionStorage.setItem(
    PENDING_KEY,
    JSON.stringify({
      state,
      clientId: metadata.clientId,
      codeVerifier,
      redirectUri: metadata.redirectUri,
      returnTo,
    } satisfies z.infer<typeof pendingSchema>)
  )

  const authorizeUrl = new URL(metadata.authorizationEndpoint)
  authorizeUrl.searchParams.set('response_type', 'code')
  authorizeUrl.searchParams.set('client_id', metadata.clientId)
  authorizeUrl.searchParams.set('redirect_uri', metadata.redirectUri)
  authorizeUrl.searchParams.set('code_challenge', challenge)
  authorizeUrl.searchParams.set('code_challenge_method', 'S256')
  authorizeUrl.searchParams.set('scope', metadata.scope)
  authorizeUrl.searchParams.set('state', state)
  authorizeUrl.searchParams.set('resource', metadata.resource)
  window.location.assign(authorizeUrl.toString())
}

// Carries the proxy's HTTP status so the caller can tell a definitive rejection
// (the AS said the grant is invalid) from a transient failure (network, 5xx).
class McpTokenRequestError extends Error {
  readonly status: number
  constructor(status: number) {
    super(`MCP token request failed (${status})`)
    this.name = 'McpTokenRequestError'
    this.status = status
  }
}

async function postToken(
  client: PortalAiClient,
  body: McpOAuthTokenRequest
): Promise<McpOAuthTokenResponse> {
  const res = await client.api.mcp.oauth.token.$post({ json: body })
  if (!res.ok) {
    throw new McpTokenRequestError(res.status)
  }
  return res.json()
}

function persistTokens(
  tokens: McpOAuthTokenResponse,
  clientId: string,
  // Refresh responses may omit refresh_token (no rotation); keep the prior one.
  fallbackRefreshToken?: string
): void {
  const next: StoredMcpTokens = {
    clientId,
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token ?? fallbackRefreshToken,
    expiresAt:
      typeof tokens.expires_in === 'number'
        ? Date.now() + tokens.expires_in * 1000
        : undefined,
  }
  tokenStore.set(next)
  emitChange()
}

/*
 * Complete the flow from the OAuth redirect: validate state, exchange the code,
 * persist tokens. Returns the path to navigate back to.
 */
export async function completeMcpAuthorization(
  client: PortalAiClient,
  params: URLSearchParams
): Promise<string> {
  const raw = sessionStorage.getItem(PENDING_KEY)
  sessionStorage.removeItem(PENDING_KEY)
  const pending = raw ? pendingSchema.safeParse(JSON.parse(raw)) : undefined
  if (!pending?.success) {
    throw new Error('No pending MCP authorization request')
  }
  const error = params.get('error')
  if (error) {
    throw new Error(`Authorization denied: ${error}`)
  }
  const code = params.get('code')
  const state = params.get('state')
  if (!code || state !== pending.data.state) {
    throw new Error('Invalid authorization response')
  }

  const tokens = await postToken(client, {
    grantType: 'authorization_code',
    clientId: pending.data.clientId,
    code,
    codeVerifier: pending.data.codeVerifier,
    redirectUri: pending.data.redirectUri,
  })
  persistTokens(tokens, pending.data.clientId)
  return pending.data.returnTo
}

/*
 * Return a valid access token for the chat request, refreshing first if it is
 * near expiry. Returns undefined when not connected; clears tokens (so the UI
 * prompts reconnect) when refresh fails.
 */
export async function getValidMcpAccessToken(
  client: PortalAiClient
): Promise<string | undefined> {
  const stored = tokenStore.get()
  if (!stored) {
    return undefined
  }
  const expired =
    stored.expiresAt !== undefined &&
    stored.expiresAt - EXPIRY_SKEW_MS <= Date.now()
  if (!expired) {
    return stored.accessToken
  }
  if (!stored.refreshToken) {
    clearMcpTokens()
    return undefined
  }
  try {
    const tokens = await postToken(client, {
      grantType: 'refresh_token',
      clientId: stored.clientId,
      refreshToken: stored.refreshToken,
    })
    persistTokens(tokens, stored.clientId, stored.refreshToken)
    return tokens.access_token
  } catch (error) {
    // Only forget the connection when the AS definitively rejected the grant
    // (expired/revoked). On transient failures (network, proxy/AS 5xx) keep the
    // tokens and skip MCP for this turn; the next turn retries the refresh.
    if (
      error instanceof McpTokenRequestError &&
      (error.status === 400 || error.status === 401)
    ) {
      clearMcpTokens()
    }
    return undefined
  }
}
